<template>
  <div class="calculations">
    <v-form>
      <v-row>
        <v-col cols="3">
          <v-select
            v-model="calcLevel"
            :items="availableCalcLevels"
            label="Calculation levels"
            outlined
            dense
          ></v-select>
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="calcType"
            :items="calcList"
            item-text="Name"
            item-value="Name"
            label="Calculation type"
            :loading="loading.calcList"
            outlined
            dense
          ></v-select>
        </v-col>
        <v-col v-if="calcType === 'Groups'" cols="3">
          <v-select
            v-model="calcGroup"
            :items="availableCalcGroups"
            item-text="Name"
            item-value="GroupId"
            label="Groups"
            outlined
            dense
          ></v-select>
        </v-col>
        <v-col v-if="calcType === 'Groups' || calcType === 'Plotting'" cols="3">
          <v-select
            v-model="calcGroupElem"
            :items="availableGroupElems"
            item-text="Name"
            item-value="Name"
            label="Group element"
            outlined
            dense
          ></v-select>
        </v-col>
        <v-col v-if="calcType === 'Calculations'" cols="3">
          <v-select
            v-model="calcElem"
            :items="availableCalcElems"
            label="Calculation elements"
            item-text="Name"
            item-value="Name"
            outlined
            dense
          ></v-select>
        </v-col>
      </v-row>
    </v-form>

    <div v-if="calcTypeQuery" class="calculationsContent">
      <div class="mr-4">
        <div class="actionBar mb-2">
          <v-btn depressed color="primary" @click="editItem(null)">
            <v-icon>mdi-plus</v-icon>
          </v-btn>

          <div class="actionBarRight">
            <v-tooltip bottom content-class="tooltip-sm">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined depressed color="primary" v-bind="attrs" v-on="on" @click="openActionPopup('copy')">
                  <v-icon>mdi-content-copy</v-icon>
                </v-btn>
              </template>
              <span>Copy</span>
            </v-tooltip>
            <v-tooltip bottom content-class="tooltip-sm">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined depressed color="primary" v-bind="attrs" v-on="on" @click="openActionPopup('export')">
                  <v-icon>mdi-export-variant</v-icon>
                </v-btn>
              </template>
              <span>Export</span>
            </v-tooltip>
            <v-tooltip bottom content-class="tooltip-sm">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined depressed color="primary" v-bind="attrs" v-on="on" @click="openActionPopup('import')">
                  <v-icon>mdi-file-import-outline</v-icon>
                </v-btn>
              </template>
              <span>Import</span>
            </v-tooltip>
          </div>
        </div>

        <v-data-table
          v-model="selectedItems"
          :items="calcItems"
          :headers="itemHeaders"
          item-key="Id"
          :items-per-page="-1"
          :loading="loading.calcItems"
          show-select
          @click:row="editItem"
          dense
        ></v-data-table>
      </div>

      <v-divider vertical></v-divider>

      <div v-if="isTestMode" class="ml-4 calcForms">
        <TestCalculationForm
          :testItem="selectedItem[0]"
          :planId="calcLevel"
          @cancelTestCalculation="onCancelTestMode"
        />
      </div>

      <div v-else class="ml-4 calcForms">
        <div v-if="inputId == null">
          <v-alert outlined type="info" dense> Please, select an item first. </v-alert>
        </div>

        <InputForm
          v-else-if="calcTypeQuery === 'Inputs'"
          :inputId="inputId"
          :planId="calcLevel"
          @reloadList="onReloadList"
        />
        <PensionElementsForm
          v-else-if="calcTypeQuery === 'Pension Elements'"
          :planId="calcLevel"
          :elementId="inputId"
          @reloadList="onReloadList"
        />
        <FormsForm
          v-else-if="calcTypeQuery === 'Forms'"
          :planId="calcLevel"
          :formId="inputId"
          @reloadList="onReloadList"
        />
        <CalculationsForm
          v-else-if="isCalcType()"
          :planId="calcLevel"
          :id="inputId"
          :type="selectedCalcType()"
          @reloadList="onReloadList"
          @setTestMode="onSetTestMode"
        />
        <ExitFormsForm
          v-else-if="calcTypeQuery === 'Exit Forms'"
          :planId="calcLevel"
          :exitId="inputId"
          :groupId="calcType === 'Groups' ? calcGroup : 0"
          @reloadList="onReloadList"
        />
        <SalaryArrayForm
          v-else-if="calcTypeQuery === 'Salary Array'"
          :planId="calcLevel"
          :itemId="inputId"
          :type="calcTypeQuery"
          @reloadList="onReloadList"
          @setTestMode="onSetTestMode"
        />
        <InvestmentForm
          v-else-if="calcTypeQuery === 'Investment'"
          :planId="calcLevel"
          :itemId="inputId"
          :type="calcTypeQuery"
          @reloadList="onReloadList"
          @setTestMode="onSetTestMode"
        />
        <LookupForm
          v-else-if="calcTypeQuery === 'Lookup'"
          :planId="calcLevel"
          :lookupId="inputId"
          :type="calcTypeQuery"
          @reloadList="onReloadList"
          @setTestMode="onSetTestMode"
        />
        <DerivationsForm
          v-else-if="calcTypeQuery === 'Derivations'"
          :planId="calcLevel"
          :derivationId="inputId"
          :type="calcTypeQuery"
          @reloadList="onReloadList"
        />
        <ElementsForm
          v-else-if="calcTypeQuery === 'Elements'"
          :planId="calcLevel"
          :elementsId="inputId"
          :groupId="calcType === 'Groups' ? calcGroup : 0"
          @reloadList="onReloadList"
        />
      </div>
    </div>

    <CalculationActionsPopup
      v-model="isActionPopupOpen"
      :button-type="currentActionType"
      :calc-level="calcLevel"
      :calc-type="calcTypeQuery"
      :calc-group="calcGroup"
      :current-plan-id="currentPlanId"
      :selected-items="actionSelectedItems"
      @completed="onActionCompleted"
    />

    <Dialog ref="confirmationDialog" v-model="dialog" />
  </div>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';
import PensionElementsForm from './CalculationForms/PensionElementsForm.vue';
import InputForm from './CalculationForms/InputForm.vue';
import FormsForm from './CalculationForms/FormsForm.vue';
import CalculationsForm from './CalculationForms/CalculationsForm.vue';
import SalaryArrayForm from './CalculationForms/SalaryArrayForm.vue';
import LookupForm from './CalculationForms/LookupForm.vue';
import ExitFormsForm from './CalculationForms/ExitFormsForm.vue';
import InvestmentForm from './CalculationForms/InvestmentForm.vue';
import DerivationsForm from './CalculationForms/DerivationsForm.vue';
import ElementsForm from './CalculationForms/ElementsForm.vue';
import TestCalculationForm from './CalculationForms/TestCalculationForm.vue';
import CalculationActionsPopup from './CalculationForms/Popups/CalculationActionsPopup.vue';

export default {
  name: 'Calculations',
  components: {
    Dialog,
    InputForm,
    PensionElementsForm,
    FormsForm,
    CalculationsForm,
    SalaryArrayForm,
    LookupForm,
    ExitFormsForm,
    InvestmentForm,
    DerivationsForm,
    ElementsForm,
    TestCalculationForm,
    CalculationActionsPopup,
  },

  data() {
    return {
      loading: {
        calcList: false,
        calcItems: false,
      },
      calcLevel: null,
      calcType: null,
      calcList: [],
      calcItems: [],
      calcGroup: null,
      calcGroupElem: null,
      calcElem: null,
      calcPlottingElem: null,
      itemHeaders: [{ text: '', value: 'ShortName', sortable: false }],
      inputId: null,
      selectedItem: [],
      selectedItems: [],
      isTestMode: false,
      isActionPopupOpen: false,
      currentActionType: null,
      dialog: false,
    };
  },

  computed: {
    currentPlanId() {
      return this.$store.getters.currentPlanId;
    },

    availableCalcLevels() {
      return [
        { text: 'System', value: 0 },
        { text: 'Current plan', value: this.currentPlanId },
      ];
    },

    availableCalcGroups() {
      if (this.calcType === 'Groups') {
        const groups = this.calcList.find((item) => item.Name === 'Groups');
        if (groups) {
          return groups.Children;
        }
      }
      return [];
    },

    availableGroupElems() {
      if (this.calcType === 'Groups' && this.calcGroup) {
        const elements = this.availableCalcGroups.find((item) => item.GroupId === this.calcGroup);
        if (elements) {
          return elements.Children;
        }
      } else if (this.calcType === 'Plotting') {
        const plotting = this.calcList.find((item) => item.Name === 'Plotting');
        if (plotting) {
          return plotting.Children;
        }
      }
      return [];
    },

    availableCalcElems() {
      const calculations = this.calcList.find((item) => item.Name === 'Calculations');
      if (calculations) {
        return calculations.Children;
      }
      return [];
    },

    isReadyToGetItems() {
      if (
        this.calcType &&
        this.calcType !== 'Groups' &&
        this.calcType !== 'Calculations' &&
        this.calcType !== 'Plotting'
      )
        return true;
      if (this.calcType === 'Calculations' && this.calcElem) return true;
      if ((this.calcType === 'Groups' || this.calcType === 'Plotting') && this.calcGroupElem)
        return true;
      return false;
    },

    calcTypeQuery() {
      if (
        this.calcType &&
        this.calcType !== 'Groups' &&
        this.calcType !== 'Calculations' &&
        this.calcType !== 'Plotting'
      )
        return this.calcType;
      if (this.calcType === 'Calculations' && this.calcElem) {
        return this.calcElem;
      }
      if ((this.calcType === 'Groups' || this.calcType === 'Plotting') && this.calcGroupElem)
        return this.calcGroupElem;
      return null;
    },

    actionSelectedItems() {
      return this.selectedItems;
    },
  },

  watch: {
    calcLevel() {
      this.getCalculationList();
      this.calcType = null;
      this.inputId = null;
      this.calcItems = [];
      this.selectedItems = [];
      this.isTestMode = false;
    },
    calcType() {
      this.calcElem = null;
      this.calcGroup = null;
      this.calcGroupElem = null;
      this.selectedItems = [];
      this.inputId = null;
      this.isTestMode = false;
    },
    calcGroupElem() {
      this.inputId = null;
      this.isTestMode = false;
      this.selectedItems = [];
    },
    calcElem() {
      this.inputId = null;
      this.isTestMode = false;
      this.selectedItems = [];
    },
    calcGroup() {
      this.calcGroupElem = null;
      this.inputId = null;
      this.isTestMode = false;
      this.selectedItems = [];
    },
    calcTypeQuery() {
      switch (this.calcTypeQuery) {
        case 'Exit Forms':
        case 'Elements':
          if (this.isReadyToGetItems) this.getGroupsOrPlottingItems();
          break;
        default:
          if (this.isReadyToGetItems) this.getCalculationItems();
          break;
      }
    },
  },

  methods: {
    openActionPopup(actionType) {
      console.log(this.selectedItems);
      this.currentActionType = actionType;
      this.isActionPopupOpen = true;
    },

    onActionCompleted(payload) {
      if (!payload || !payload.type) return;

      if (payload.type === 'copy') {
        this.onReloadList('update');
        return;
      }

      if (payload.type === 'import') {
        this.onReloadList('update');
      }
    },

    selectedCalcType() {
      if (this.selectedItem.length > 0)
        if (this.selectedItem[0] == null) return this.calcTypeQuery;
        else if ('Description' in this.selectedItem[0]) {
          return this.selectedItem[0].Description;
        }
      return 'Other';
    },
    isCalcType() {
      if (this.selectedItem.length > 0)
        if (this.selectedItem[0] == null)
          return (
            this.calcTypeQuery === 'Date' ||
            this.calcTypeQuery === 'Service' ||
            this.calcTypeQuery === 'Other'
          );
        else if ('Description' in this.selectedItem[0]) {
          return (
            this.selectedItem[0].Description === 'Other' ||
            this.selectedItem[0].Description === 'Date' ||
            this.selectedItem[0].Description === 'Service'
          );
        }
      return false;
    },
    editItem(item) {
      this.selectedItem = [item];
      this.inputId = item ? item.Id : 0;
    },

    onReloadList(val, id) {
      if (val === 'delete') {
        this.inputId = null;
        this.selectedItem = [];
      } else if (val === 'update' && !!id) {
        this.inputId = id;
        this.selectedItem = [this.calcItems.find((item) => item.Id === this.inputId)];
      }

      switch (this.calcTypeQuery) {
        case 'Exit Forms':
        case 'Elements':
          this.getGroupsOrPlottingItems();
          break;
        default:
          this.getCalculationItems(id);
          break;
      }
    },

    onSetTestMode() {
      this.isTestMode = true;
    },

    onCancelTestMode() {
      this.isTestMode = false;
    },

    async getCalculationList() {
      this.loading.calcList = true;
      try {
        const response = await this.$api.get(
          `/Calculation/GetCalculationList?planId=${this.calcLevel}`
        );

        this.calcList = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading.calcList = false;
      }
    },

    async getCalculationItems(calcId = 0) {
      this.loading.calcItems = true;

      try {
        const response = await this.$api.get(
          `/Calculation/GetCalculationItems?planId=${this.calcLevel}&calcType=${this.calcTypeQuery}&groupId=0`
        );

        this.calcItems = response.data;

        if (calcId) {
          this.selectedItem = [this.calcItems.find((item) => item.Id === calcId)];
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading.calcItems = false;
      }
    },

    async getGroupsOrPlottingItems() {
      this.loading.calcItems = true;

      let url, groupId;

      if (this.calcType === 'Plotting') groupId = 0;
      if (this.calcType === 'Groups') {
        groupId = this.calcGroup;
      }

      if (this.calcTypeQuery === 'Exit Forms') {
        url = `/CalculationPlotting/GetPlottingForms?planId=${this.currentPlanId}&groupId=${groupId}`;
      } else if (this.calcTypeQuery === 'Elements') {
        url = `/CalculationElements/GetPlottingForms?planId=${this.currentPlanId}&groupId=${groupId}`;
      }

      try {
        const response = await this.$api.get(url);

        if (response.data.length) {
          this.calcItems = response.data;
        } else {
          this.calcItems = [];
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading.calcItems = false;
      }
    },
  },
};
</script>

<style scoped>
.calculations {
  margin-top: 20px;
  margin-bottom: 20px;
  padding-left: 30px;
  padding-right: 30px;
}
.calculationsContent {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
}
.actionBar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.actionBarRight {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}
.calculations tr:hover {
  cursor: pointer;
}
.calcForms,
.calcFormItem {
  height: 100%;
}
.calcForms {
  max-height: calc(100vh - 10.25rem);
  overflow-y: auto;
  scrollbar-width: thin;
  padding-right: 8px;
  padding-top: 4px;
}
.calcForms::-webkit-scrollbar {
  width: 4px;
}
.calcForms::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
}
.calcForms::-webkit-scrollbar-thumb {
  background-color: darkgrey;
}
.calculationsContent {
  padding-top: 4px;
}
</style>

<style>
.v-tooltip__content.tooltip-sm {
  font-size: 11px;
  padding: 2px 6px;
}
</style>
